import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'

import { Button, CircleCheckBigIcon, CopyIcon } from '@buffer-mono/popcorn'

import { logError } from '~publish/legacy/utils/logError'

import styles from './CopyButton.module.css'

const COPIED_FEEDBACK_TIMEOUT_MS = 5000

interface CopyButtonProps {
  textToCopy: string
  label?: string
  className?: string
  onCopy?: () => void
}

export function CopyButton({
  textToCopy,
  label = 'Copy',
  className,
  onCopy,
}: CopyButtonProps): JSX.Element {
  const [isCopied, setIsCopied] = useState(false)

  useEffect(() => {
    if (!isCopied) {
      return undefined
    }

    const timer = setTimeout(() => {
      setIsCopied(false)
    }, COPIED_FEEDBACK_TIMEOUT_MS)

    return (): void => {
      clearTimeout(timer)
    }
  }, [isCopied])

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(textToCopy)
      setIsCopied(true)
      onCopy?.()
    } catch (error) {
      logError(error, { context: 'copyTextToClipboard' })
    }
  }

  return (
    <Button
      variant="tertiary"
      className={`${isCopied ? styles.copiedButton : ''} ${className ?? ''}`}
      onClick={handleCopy}
      aria-label={isCopied ? 'Copied' : label}
    >
      <AnimatePresence mode="wait" initial={false}>
        {isCopied ? (
          <>
            <m.span
              key="check"
              initial={{ opacity: 0.5 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0.5 }}
              transition={{ duration: 0.1 }}
              className={styles.iconWrapper}
            >
              <CircleCheckBigIcon />
            </m.span>
            <m.span
              initial={{ maxWidth: 0, opacity: 0 }}
              animate={{ maxWidth: 100, opacity: 1 }}
              exit={{ maxWidth: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className={styles.copiedText}
            >
              Copied!
            </m.span>
          </>
        ) : (
          <m.span
            key="copy"
            initial={{ opacity: 0.5 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0.5 }}
            transition={{ duration: 0.1 }}
            className={styles.iconWrapper}
          >
            <CopyIcon />
          </m.span>
        )}
      </AnimatePresence>
    </Button>
  )
}
